import { Button, Container, Flex, Group, Modal, Stack, Text, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { v4 as uuidv4 } from 'uuid'
import { useContext, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { IconTrash, IconFilePlus } from '@tabler/icons-react'
import { PageContainer } from '@shared/components/app-shell/page-container/page-container'
import { SpeciTable } from '@/components/speci/speci-table/speci-table'
import {
  useAuthenticatedGraphMutation,
  useAuthenticatedGraphQuery,
} from '@/hooks/use-authenticated-query'
import { ListSpeciGql } from '@/graphql/queries/speci/list-speci.graphql'
import { DeleteSpeciGql } from '@/graphql/mutations/speci/speci-delete.graphql'
import { showSuccessNotification } from '@shared/helpers/notifications/notifications'
import { GrowerServiceQuery } from '@grower-api-types'
import { PAGE_TITLES } from '@/constants/page-titles'
import { StationContext } from '@/pages/station-context'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { useNavigation } from '@/hooks/use-navigation'

type SpeciToDelete = {
  id: string
  author: string
  created: string
} | null

export function WoolSpecificationsPage() {
  const { station } = useContext(StationContext)
  const queryClient = useQueryClient()
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false)
  const [speciToDelete, setSpeciToDelete] = useState<SpeciToDelete>(null)
  const { navigateToNewSpeci } = useNavigation()

  const startSpeci = () => {
    const newSpeciID = uuidv4()
    navigateToNewSpeci(station.recordNumber, newSpeciID)
  }

  const { data, isError, isLoading } = useAuthenticatedGraphQuery({
    queryKey: ['listSpecis', station.recordNumber],
    gql: ListSpeciGql,
    queryParams: {
      stationRecordNumber: station.recordNumber,
    },
  })

  // Mutation to delete the speci
  const deleteSpeciMutation = useAuthenticatedGraphMutation({
    gql: DeleteSpeciGql,
    gqlKey: 'deleteSpeci',
    onSuccess: () => {
      showSuccessNotification('Wool Specification deleted')
    },
  })

  const askForDeleteConfirmation = (speciId: string) => {
    const speci = data?.listSpecis?.results?.find((speci) => speci.id === speciId)
    setSpeciToDelete(
      speci
        ? {
            id: speciId,
            author: speci.author,
            created: speci.created,
          }
        : null
    )
    openDeleteModal()
  }

  const cancelDeleteSpeci = () => {
    setSpeciToDelete(null)
    closeDeleteModal()
  }

  const confirmDeleteSpeci = () => {
    const queryKey = ['listSpecis', station.recordNumber]
    const cacheBeforeDelete = queryClient.getQueryData(queryKey) as GrowerServiceQuery

    const resultsWithTargetSpeciRemoved = cacheBeforeDelete.listSpecis?.results?.filter(
      (speci: any) => speci.id !== speciToDelete?.id
    )
    const cacheAfterDelete = {
      ...cacheBeforeDelete,
      listSpecis: { results: resultsWithTargetSpeciRemoved },
    } as GrowerServiceQuery

    queryClient.setQueryData(queryKey, () => cacheAfterDelete)

    if (speciToDelete?.id) {
      deleteSpeciMutation.mutate(
        { speciId: speciToDelete.id },
        {
          onError: () => {
            queryClient.setQueryData(queryKey, () => cacheBeforeDelete)
          },
        }
      )
    }
    setSpeciToDelete(null)
    closeDeleteModal()
  }

  return (
    <PageContainer title={PAGE_TITLES.WOOL_SPECIFICATIONS}>
      <Container p={0}>
        <Stack gap='xl'>
          <Stack gap='md'>
            <Title order={2}>Wool Specifications</Title>
            <Text>
              Create an electronic wool specification which will automatically be sent to your Area
              Manager on completion, as well as being stored here for filing.
            </Text>
            <Text>
              You can save your progress to come back at a later stage. In progress specifications
              are displayed below.
            </Text>
          </Stack>
          <Stack align='start'>
            <Button size='md' onClick={startSpeci} leftSection={<IconFilePlus />}>
              Create wool specification
            </Button>
          </Stack>
          <Stack gap='md'>
            <Title order={2}>Specifications for {station.brand}</Title>
            <SpeciTable
              data={data?.listSpecis?.results ?? []}
              isLoading={isLoading}
              isError={isError}
              deleteSpeci={askForDeleteConfirmation}
            />
          </Stack>
        </Stack>
      </Container>

      <Modal
        data-testid='delete-speci-modal'
        title={
          <Flex justify='flex-start' align='center'>
            <IconTrash size={28} color='var(--mantine-color-gray-4)' />
            <Text fw={500} size='lg' ml='sm'>
              Delete wool specification
            </Text>
          </Flex>
        }
        opened={isDeleteModalOpen}
        onClose={closeDeleteModal}
        centered
        closeButtonProps={{ 'aria-label': 'Close modal' }}
        returnFocus={false}
      >
        Are you sure you want to delete this specification?
        {speciToDelete && (
          <Text size='sm' c='dimmed' mt='xs'>
            Created by {speciToDelete.author} on{' '}
            {formatDate(new Date(speciToDelete.created), 'DD/MM/YYYY')}
          </Text>
        )}
        <Group justify='flex-end' mt='md' gap='xs'>
          <Button variant='transparent' onClick={cancelDeleteSpeci}>
            No
          </Button>
          <Button color='red' onClick={confirmDeleteSpeci}>
            Yes, delete
          </Button>
        </Group>
      </Modal>
    </PageContainer>
  )
}
